/**
 * Sync as the app uses it (issue #25): one engine for this install, wired to the browser's
 * `fetch`, this build's Snapshot and the Snapshot migration of #17, and the handling of the
 * sign-in redirect the Worker sends the browser back with.
 *
 * The screens are #26's; everything they need is on the engine this module hands out.
 */
import { SIGN_IN_RETURN_ROUTE } from "../../shared/sync-format";
import type { Character } from "../character/model";
import { loadManifest } from "../data/manifest";
import { appSettle, loadMigrationTarget, migrateToSnapshot } from "../migration";
import { createEngine, type EngineEnv, type SyncEngine } from "./engine";

export type { ConflictCopy, DeletedRecord, ParkedRecord, RestoreOutcome, SyncEngine, SyncOutcome } from "./engine";
export type { ParkReason, SyncRecord, SyncSession } from "./model";
export type { SyncStatus } from "./status";
export { describeSyncFix, describeSyncStatus, syncIndicator } from "./status";
export { describeParkFix, describeParkReason, timeStamp } from "./rules";
export { SyncError, SyncOffline } from "./client";

/** How long a save waits before its push goes out, so a burst of edits is one push. */
export const PUSH_DEBOUNCE_MS = 3000;

async function migrateIncoming(character: Character): Promise<Character> {
  const target = await loadMigrationTarget();
  return (await migrateToSnapshot(character, target, appSettle)).character;
}

export function browserEnv(): EngineEnv {
  return {
    // The app and the Worker share an origin (`assets.run_worker_first`, ADR-0017).
    transport: { origin: "", fetch: (url, init) => fetch(url, init) },
    now: () => Date.now(),
    snapshotTag: async () => (await loadManifest()).snapshot.release,
    migrate: migrateIncoming,
    debounceMs: PUSH_DEBOUNCE_MS,
  };
}

let engine: SyncEngine | undefined;

/** This install's engine. */
export function appEngine(): SyncEngine {
  engine ??= createEngine(browserEnv());
  return engine;
}

/** What the Worker sent the browser back with (`shared/sync-format.ts`, `SIGN_IN_RETURN_ROUTE`). */
export type SignInRedirect = { claim: string } | { error: string };

/** Read the sign-in redirect out of a hash. Null when the hash is not one. */
export function readSignInRedirect(hash: string): SignInRedirect | null {
  if (!hash.startsWith(SIGN_IN_RETURN_ROUTE)) return null;
  const query = hash.slice(SIGN_IN_RETURN_ROUTE.length);
  const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
  const claim = params.get("claim");
  if (claim) return { claim };
  return { error: params.get("error") ?? "sign_in_failed" };
}

/** A name for this Device that the person will recognise on the Account screen. */
export function defaultDeviceName(agent: string = typeof navigator === "undefined" ? "" : navigator.userAgent): string {
  if (/iPad/i.test(agent)) return "iPad";
  if (/iPhone/i.test(agent)) return "iPhone";
  if (/Android/i.test(agent)) return "Android phone";
  if (/Macintosh/i.test(agent)) return "Mac";
  if (/Windows/i.test(agent)) return "Windows PC";
  return "This device";
}

/**
 * Why the last sign-in did not end in a Device Token, for the sign-in card to explain (#26).
 * Kept beside the status rather than in it: the sync that follows a failed sign-in sets the
 * status straight back to "off", and the person still has to be told what happened.
 */
export interface SignInProblem {
  /** The Worker's code, or "claim_failed" when spending the claim itself did not work. */
  code: string;
  message: string;
  at: number;
}

let problem: SignInProblem | null = null;
const problemWatchers = new Set<(p: SignInProblem | null) => void>();

export function signInProblem(): SignInProblem | null {
  return problem;
}

export function subscribeSignInProblem(listener: (p: SignInProblem | null) => void): () => void {
  problemWatchers.add(listener);
  return () => problemWatchers.delete(listener);
}

function setSignInProblem(next: SignInProblem | null): void {
  problem = next;
  for (const listener of [...problemWatchers]) listener(next);
}

/** Dismiss what the card is explaining. */
export function clearSignInProblem(): void {
  setSignInProblem(null);
}

/** What the Worker's sign-in codes mean to the person (`SyncErrorCode`). */
export function describeSignInProblem(code: string): string {
  if (code === "sign_in_failed") return "The sign-in did not finish: it was refused, or it took too long. Try again.";
  if (code === "server_misconfigured") return "Sync is not set up on the server yet, so signing in is not possible. This device keeps working.";
  if (code === "claim_failed") return "The sign-in came back, but this device could not finish it — the return took more than five minutes, or it was already used. Try again.";
  return "The sign-in did not finish. Try again.";
}

/** Where the app was when the sign-in left it, so the return lands back on the same screen (#26). */
const RETURN_KEY = "sync:return";

function rememberRoute(hash: string): void {
  try {
    sessionStorage.setItem(RETURN_KEY, hash.startsWith("#/") && !hash.startsWith(SIGN_IN_RETURN_ROUTE) ? hash : "#/");
  } catch {
    /* private mode: the return lands on the home screen */
  }
}

function takeRoute(): string {
  let kept: string | null = null;
  try {
    kept = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    /* private mode */
  }
  return kept && kept.startsWith("#/") ? kept : "#/";
}

/** Send the browser to the issuer. A top-level navigation: the service worker passes `/api/` through (#3). */
export async function startSignIn(device = defaultDeviceName()): Promise<void> {
  const url = await appEngine().signInUrl(device);
  clearSignInProblem();
  rememberRoute(location.hash);
  location.assign(url);
}

/**
 * Start syncing: spend the claim if this launch is the sign-in coming back, then watch for
 * saves, follow the foreground and sync once. Never throws; a Device that cannot reach the
 * Worker carries on offline.
 */
export async function startSync(): Promise<void> {
  const app = appEngine();
  const redirect = readSignInRedirect(location.hash);
  if (redirect) {
    // The claim is single use and must not stay in the history; the app lands back on the
    // screen the sign-in left from.
    const back = takeRoute();
    history.replaceState(null, "", `${location.pathname}${location.search}${back}`);
    // `replaceState` fires no event of its own, and the router listens for one (`src/ui/router.ts`).
    dispatchEvent(new Event("hashchange"));
    if ("claim" in redirect) {
      try {
        await app.completeSignIn(redirect.claim);
      } catch (e) {
        console.warn("the sign-in could not be completed", e);
        setSignInProblem({ code: "claim_failed", message: describeSignInProblem("claim_failed"), at: Date.now() });
      }
    } else {
      console.warn(`the sign-in did not finish: ${redirect.error}`);
      setSignInProblem({ code: redirect.error, message: describeSignInProblem(redirect.error), at: Date.now() });
    }
  }
  try {
    await app.start();
  } catch (e) {
    console.warn("sync could not start", e);
  }
}
